/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {callOpenRouterApi} from '../../services/openrouterApi';
import {UserProfile, ChatMessage as ChatMessageType} from '../../types';
import {supabase} from '../../services/supabase';
import ChatMessage from '../common/ChatMessage';
import InputField from '../common/InputField';
import MarkdownRenderer from '../common/MarkdownRenderer';
import {useTTS} from '../../contexts/TTSContext';
import {CreditService} from '../../services/creditService';
import SubscriptionModal from '../common/SubscriptionModal';
import '../../styles/components/DocumentQuestionExtractor.css';
// Import libraries for document extraction
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface ExtractedQuestion {
  id: string;
  question: string;
  options?: string[];
  correctAnswer?: number | boolean | string;
  explanation?: string;
  type: 'mcq' | 'theory' | 'true-false' | 'fill-blank' | 'matching';
  difficulty?: 'easy' | 'medium' | 'hard';
  topic?: string;
  pageReference?: number;
  sourceText?: string;
  concept_tested?: string;
}

interface DocumentQuestionState {
  questions: ExtractedQuestion[];
  currentQuestion: number;
  answers: any[];
  isComplete: boolean;
  score?: number;
  startTime: Date;
  endTime: Date | null;
  showResults: boolean;
  selectedQuestions: Set<string>;
  documentInfo: {
    filename: string;
    fileSize: number;
    fileType: string;
    pageCount?: number;
    processedAt: Date;
  } | null;
  timeLimit: number;
  timeLeft: number;
  // New state to track if answer is selected for current question
  showExplanationForCurrent: boolean;
}

interface DocumentSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  documentName: string;
  questionCount: number;
  questionTypes: string[];
  isTemporary?: boolean;
  processing_completed?: boolean;
  extracted_questions?: ExtractedQuestion[];
  document_summary?: string;
  key_topics?: string[];
  difficulty_assessment?: string;
  estimated_study_time?: number;
}

interface DocumentExtractorConfig {
  documentContent: string;
  documentName: string;
  questionTypes: {
    mcq: boolean;
    theory: boolean;
    trueFalse: boolean;
    fillBlank: boolean;
    matching: boolean;
  };
  questionCount: number;
  difficultyLevel: 'easy' | 'medium' | 'hard' | 'mixed';
  includeExplanations: boolean;
  focusTopics: string[];
  excludeTopics: string[];
  questionFormat: 'exam' | 'practice' | 'quiz' | 'homework';
  language: string;
  timeLimit: number;
}

interface DocumentQuestionExtractorProps {
  userProfile: UserProfile;
  sessionId?: string;
}

const DocumentQuestionExtractor: React.FC<DocumentQuestionExtractorProps> = ({userProfile, sessionId}) => {
  // State declarations (keep all existing state)
  const [sessions, setSessions] = useState<DocumentSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [searchQuery, setSearchQuery] = useState('');

  // Credit and subscription states
  const [userCredits, setUserCredits] = useState<number>(0);
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(true);

  // Document upload states
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Configuration states
  const [config, setConfig] = useState<DocumentExtractorConfig>({
    documentContent: '',
    documentName: '',
    questionTypes: {
      mcq: true,
      theory: false,
      trueFalse: false,
      fillBlank: false,
      matching: false
    },
    questionCount: 10,
    difficultyLevel: 'medium',
    includeExplanations: true,
    focusTopics: [],
    excludeTopics: [],
    questionFormat: 'practice',
    language: 'English',
    timeLimit: 30
  });
  // Temporary state for inputs
  const [tempQuestionCount, setTempQuestionCount] = useState<string>('10');
  const [tempFocusTopics, setTempFocusTopics] = useState<string>('');
  const [tempExcludeTopics, setTempExcludeTopics] = useState<string>('');
  const [tempTimeLimit, setTempTimeLimit] = useState<string>('30');
  // Question extraction state
  const [questionState, setQuestionState] = useState<DocumentQuestionState | null>(null);
  const [extractionStarted, setExtractionStarted] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  // Timer reference
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Chat functionality
  const [chatInput, setChatInput] = useState('');
  const {speak, isMessageSpeaking} = useTTS();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Refs
  const isMountedRef = useRef(true);
  const processingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // ===== SYNC EFFECTS =====
  useEffect(() => {
    setTempQuestionCount(config.questionCount.toString());
    setTempFocusTopics(config.focusTopics.join(', '));
    setTempExcludeTopics(config.excludeTopics.join(', '));
    setTempTimeLimit(config.timeLimit.toString());
  }, [config.questionCount, config.focusTopics, config.excludeTopics, config.timeLimit]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  useEffect(() => {
    isMountedRef.current = true;

    const loadData = async () => {
      if (isMountedRef.current) {
        await loadUserCredits();
        await loadSessions();
      }
    };

    loadData();

    const handleResize = () => {
      if (isMountedRef.current) {
        setSidebarOpen(window.innerWidth > 768);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);
  
  // Timer effect for exam
  useEffect(() => {
    if (questionState && !questionState.isComplete && questionState.timeLeft > 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      timerIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          setQuestionState(prev => {
            if (!prev) return prev;

            const newTimeLeft = prev.timeLeft - 1;
            if (newTimeLeft <= 0) {
              handleSubmitExam();
              return {
                ...prev,
                timeLeft: 0,
                isComplete: true,
                endTime: new Date()
              };
            }

            return {
              ...prev,
              timeLeft: newTimeLeft
            };
          });
        }
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [questionState?.isComplete]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };
  
  // ===== IMPROVED DOCUMENT EXTRACTION =====
  const extractTextFromFile = async (file: File): Promise<string> => {
    try {
      // Handle different file types
      if (file.name.endsWith('.txt') || file.name.endsWith('.md') || file.name.endsWith('.csv')) {
        return await extractTextFromTextFile(file);
      } else if (file.name.endsWith('.docx')) {
        return await extractTextFromDocx(file);
      } else if (file.name.endsWith('.pdf')) {
        return await extractTextFromPdf(file);
      } else {
        throw new Error(`Unsupported file type: ${file.name}`);
      }
    } catch (error) {
      console.error('Error extracting text:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to extract text from ${file.name}: ${errorMessage}`);
    }
  };
  
  const extractTextFromTextFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string || '');
      };
      reader.onerror = () => reject(new Error('Failed to read text file'));
      reader.readAsText(file, 'utf-8');
    });
  };
  
  const extractTextFromDocx = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const result = await mammoth.extractRawText({ arrayBuffer });

          // Clean up the extracted text
          const text = result.value
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\n\s*\n/g, '\n\n') // Clean up multiple newlines
            .trim();

          if (!text) {
            reject(new Error('No text content found in DOCX file'));
          } else {
            resolve(text);
          }
        } catch (error) {
          console.error('DOCX extraction error:', error);
          reject(new Error('Failed to extract text from DOCX file. The file might be corrupted or password protected.'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read DOCX file'));
      reader.readAsArrayBuffer(file);
    });
  };
  
  const extractTextFromPdf = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

          let fullText = '';

          // Extract text from each page
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
              .map((item: any) => item.str)
              .join(' ')
              .replace(/\s+/g, ' ');

            fullText += pageText + '\n\n';

            // Update progress
            if (isMountedRef.current) {
              setProcessingProgress(Math.round((i / pdf.numPages) * 50)); // First 50% for extraction
            }
          }

          if (!fullText.trim()) {
            reject(new Error('No text content found in PDF file. The file might be scanned or image-based.'));
          } else {
            resolve(fullText.trim());
          }
        } catch (error) {
          console.error('PDF extraction error:', error);
          reject(new Error('Failed to extract text from PDF file. The file might be corrupted, password protected, or scanned.'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read PDF file'));
      reader.readAsArrayBuffer(file);
    });
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ===== CREDIT FUNCTIONS =====
  const loadUserCredits = async () => {
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        setIsCheckingCredits(false);
        return;
      }
      const {data: profile, error} = await supabase
        .from('profiles')
        .select('credits, subscription_tier')
        .eq('id', user.id)
        .single();
      if (error) {
        console.error('Error loading user credits:', error);
        setIsCheckingCredits(false);
        return;
      }
      if (isMountedRef.current) {
        setUserCredits(profile?.credits || 0);
        setSubscriptionTier(profile?.subscription_tier || 'free');
        setIsCheckingCredits(false);
      }
    } catch (error) {
      console.error('Error in loadUserCredits:', error);
      setIsCheckingCredits(false);
    }
  };
  
  const checkProcessingCredits = async (): Promise<boolean> => {
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        setErrorMessage('Please log in to process documents');
        return false;
      }
      const creditCost = calculateCreditCost();
      const currentCredits = userCredits;

      if (currentCredits < creditCost) {
        setErrorMessage(`You need at least ${creditCost} credit(s) to process this document. You have ${currentCredits} credits.`);
        setShowSubscriptionModal(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking credits:', error);
      setErrorMessage('Error checking credits. Please try again.');
      return false;
    }
  };
  
  const calculateCreditCost = (): number => {
    let cost = 1;

    if (uploadedFile) {
      if (uploadedFile.size > 1024 * 1024) {
        cost += Math.floor(uploadedFile.size / (1024 * 1024));
      }
    }

    if (config.questionCount > 20) {
      cost += Math.floor((config.questionCount - 20) / 10);
    }

    return Math.min(cost, 5);
  };
  
  // ===== SESSION MANAGEMENT =====
  const loadSessions = async () => {
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        return;
      }
      const {data: sessionData, error: sessionError} = await supabase
        .from('document_question_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', {ascending: false});
      if (sessionError) {
        console.error('Error loading sessions:', sessionError);
        return;
      }
      const loadedSessions: DocumentSession[] = [];

      for (const session of sessionData || []) {
        try {
          const {data: msgs, error: msgError} = await supabase
            .from('document_question_messages')
            .select('*')
            .eq('session_id', session.id)
            .order('created_at', {ascending: true});
          const sessionObj: DocumentSession = {
            id: session.id,
            title: session.title || 'Untitled Session',
            timestamp: new Date(session.created_at),
            messages: msgs?.map(msg => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.created_at),
              chat_type: 'document-questions',
            })) || [],
            documentName: session.document_name || 'Unknown document',
            questionCount: session.question_count || 0,
            questionTypes: session.question_types || [],
            processing_completed: session.processing_completed || false,
            extracted_questions: session.extracted_questions || [],
            document_summary: session.document_summary || '',
            key_topics: session.key_topics || [],
            difficulty_assessment: session.difficulty_assessment,
            estimated_study_time: session.estimated_study_time,
            isTemporary: false
          };

          loadedSessions.push(sessionObj);
        } catch (error) {
          console.error('Error processing session:', error);
        }
      }
      if (isMountedRef.current) {
        setSessions(loadedSessions);
        if (loadedSessions.length > 0) {
          const initialSession = sessionId
            ? loadedSessions.find(s => s.id === sessionId) || loadedSessions[0]
            : loadedSessions[0];
          setCurrentSessionId(initialSession.id);
          setMessages(initialSession.messages);

          if (initialSession.processing_completed && initialSession.extracted_questions) {
            setExtractionStarted(true);
            setQuestionState({
              questions: initialSession.extracted_questions,
              currentQuestion: 0,
              answers: [],
              isComplete: false,
              startTime: new Date(),
              endTime: null,
              showResults: false,
              selectedQuestions: new Set(initialSession.extracted_questions.map(q => q.id)),
              documentInfo: null,
              timeLimit: config.timeLimit,
              timeLeft: config.timeLimit * 60,
              showExplanationForCurrent: false
            });
          }
        } else {
          createNewSession();
        }
      }
    } catch (error) {
      console.error('Error in loadSessions:', error);
    }
  };
  
  const createNewSession = () => {
    const tempSession: DocumentSession = {
      id: `temp-${Date.now()}`,
      title: 'New Document Questions',
      timestamp: new Date(),
      messages: [],
      documentName: '',
      questionCount: 0,
      questionTypes: [],
      isTemporary: true,
      processing_completed: false,
    };

    if (isMountedRef.current) {
      setSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSession.id);
      setMessages([]);
      setUploadedFile(null);
      setFileContent('');
      setExtractionStarted(false);
      setQuestionState(null);
    }
  };
  
  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    if (!sessionId || sessionId.startsWith('temp-')) {
      return null;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user');
        return null;
      }
      const { data, error } = await supabase
        .from('document_question_messages')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          role,
          content,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) {
        console.error('Error saving message:', error);
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error in saveChatMessage:', error);
      return null;
    }
  };
  
  const saveSession = async (session: DocumentSession) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      setErrorMessage('Failed to authenticate user. Please log in again.');
      return null;
    }
    try {
      const { data, error } = await supabase
        .from('document_question_sessions')
        .insert({
          user_id: user.id,
          title: session.title,
          created_at: session.timestamp.toISOString(),
          document_name: session.documentName,
          question_count: session.questionCount,
          question_types: session.questionTypes,
          processing_completed: session.processing_completed || false,
          extracted_questions: session.extracted_questions || [],
          document_summary: session.document_summary || '',
          key_topics: session.key_topics || [],
          difficulty_assessment: session.difficulty_assessment,
          estimated_study_time: session.estimated_study_time,
        })
        .select()
        .single();
      if (error) {
        console.error('Error creating session:', error);
        setErrorMessage('Error creating session: ' + error.message);
        return null;
      }
      return { ...session, id: data.id, isTemporary: false };
    } catch (error) {
      console.error('Error in saveSession:', error);
      return null;
    }
  };
  
  const updateSessionResults = async (sessionId: string, results: any) => {
    if (!sessionId || sessionId.startsWith('temp-')) return;
    try {
      const { error } = await supabase
        .from('document_question_sessions')
        .update(results)
        .eq('id', sessionId);
      if (error) {
        console.error('Error updating session results:', error);
      }
    } catch (error) {
      console.error('Error in updateSessionResults:', error);
    }
  };
  
  // ===== FILE HANDLING =====
  const handleFileSelect = (file: File) => {
    const allowedTypes = [
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/markdown',
      'text/csv'
    ];

    // Check by extension as well
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const allowedExts = ['txt', 'pdf', 'doc', 'docx', 'md', 'csv'];

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt || '')) {
      setErrorMessage('Unsupported file type. Please upload TXT, PDF, DOC, DOCX, MD, or CSV files.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('File too large. Maximum size is 10MB.');
      return;
    }
    setUploadedFile(file);
    setConfig(prev => ({
      ...prev,
      documentName: file.name
    }));

    // Clear any previous errors
    setErrorMessage('');
    setErrorDetails('');

    // Show file selected message
    const successMsg = `✅ File selected: ${file.name} (${formatFileSize(file.size)})`;
    console.log(successMsg);
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  
  const removeFile = () => {
    setUploadedFile(null);
    setFileContent('');
    setConfig(prev => ({
      ...prev,
      documentContent: '',
      documentName: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // ===== CONFIGURATION HANDLERS =====
  const handleQuestionTypeChange = (type: keyof typeof config.questionTypes) => {
    setConfig(prev => ({
      ...prev,
      questionTypes: {
        ...prev.questionTypes,
        [type]: !prev.questionTypes[type]
      }
    }));
  };
  
  const handleQuestionCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempQuestionCount(value);

    if (value !== '') {
      const num = Math.min(50, Math.max(1, parseInt(value) || 10));
      setConfig(prev => ({...prev, questionCount: num}));
    }
  };
  
  const handleQuestionCountBlur = () => {
    if (tempQuestionCount === '') {
      const defaultValue = 10;
      setTempQuestionCount(defaultValue.toString());
      setConfig(prev => ({...prev, questionCount: defaultValue}));
    }
  };
  
  const handleTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempTimeLimit(value);

    if (value !== '') {
      const num = Math.min(180, Math.max(5, parseInt(value) || 30));
      setConfig(prev => ({...prev, timeLimit: num}));
    }
  };
  
  const handleTimeLimitBlur = () => {
    if (tempTimeLimit === '') {
      const defaultValue = 30;
      setTempTimeLimit(defaultValue.toString());
      setConfig(prev => ({...prev, timeLimit: defaultValue}));
    }
  };
  
  const handleFocusTopicsBlur = () => {
    const topics = tempFocusTopics
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    setConfig(prev => ({...prev, focusTopics: topics}));
  };
  
  const handleExcludeTopicsBlur = () => {
    const topics = tempExcludeTopics
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    setConfig(prev => ({...prev, excludeTopics: topics}));
  };
  
  const handleDifficultyChange = (difficulty: 'easy' | 'medium' | 'hard' | 'mixed') => {
    setConfig(prev => ({...prev, difficultyLevel: difficulty}));
  };
  
  const handleFormatChange = (format: 'exam' | 'practice' | 'quiz' | 'homework') => {
    setConfig(prev => ({...prev, questionFormat: format}));
  };
  
  const validateConfig = () => {
    setErrorDetails('');
    if (!uploadedFile) {
      setErrorMessage('Please upload a document first');
      return false;
    }
    const selectedTypes = Object.values(config.questionTypes).filter(Boolean).length;
    if (selectedTypes === 0) {
      setErrorMessage('Please select at least one question type');
      return false;
    }
    if (config.questionCount < 1 || config.questionCount > 50) {
      setErrorMessage('Number of questions must be between 1 and 50');
      return false;
    }
    if (config.timeLimit < 5 || config.timeLimit > 180) {
      setErrorMessage('Time limit must be between 5 and 180 minutes');
      return false;
    }
    return true;
  };
  
  // ===== PROCESSING =====
  const startProgressSimulation = () => {
    setProcessingProgress(0);
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
    }

    processingIntervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        setProcessingProgress(prev => {
          const increment = Math.random() * 5;
          const newProgress = Math.min(prev + increment, 90);
          return newProgress;
        });
      }
    }, 300);
  };
  
  // ===== QUESTION EXTRACTION =====
  const handleExtractQuestions = async () => {
    if (!validateConfig()) return;

    const creditCost = calculateCreditCost();
    const hasEnoughCredits = await checkProcessingCredits();
    if (!hasEnoughCredits) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');
    setErrorDetails('');
    startProgressSimulation();

    let newSessionId = currentSessionId;
    let isNewSession = messages.length === 0 && sessions.find(s => s.id === currentSessionId)?.isTemporary;
    const title = `Questions from ${uploadedFile!.name}`;
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        setErrorMessage('User not authenticated');
        setIsProcessing(false);
        return;
      }
      // Add user message
      const userMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'user',
        content: `📄 Processing document: **${uploadedFile!.name}**\n\n**Configuration:**\n- Question Types: ${Object.entries(config.questionTypes).filter(([_, enabled]) => enabled).map(([type]) => type).join(', ')}\n- Number of Questions: ${config.questionCount}\n- Difficulty: ${config.difficultyLevel}\n- Format: ${config.questionFormat}\n- Time Limit: ${config.timeLimit} minutes\n- Focus Topics: ${config.focusTopics.length > 0 ? config.focusTopics.join(', ') : 'All topics'}`,
        timestamp: new Date(),
        chat_type: 'document-questions',
      };
      const newMessages = [...messages, userMessage];
      if (isMountedRef.current) {
        setMessages(newMessages);
      }
      await saveChatMessage(newSessionId, 'user', userMessage.content);
      // Save session if new
      if (isNewSession) {
        const tempSession = sessions.find(s => s.id === currentSessionId);
        if (tempSession) {
          const sessionToSave: DocumentSession = {
            ...tempSession,
            title,
            documentName: uploadedFile!.name,
            questionCount: config.questionCount,
            questionTypes: Object.entries(config.questionTypes)
              .filter(([_, enabled]) => enabled)
              .map(([type]) => type)
          };
          const savedSession = await saveSession(sessionToSave);
          if (!savedSession) {
            setIsProcessing(false);
            return;
          }
          newSessionId = savedSession.id;
          if (isMountedRef.current) {
            setSessions(prev => prev.map(s => s.id === currentSessionId ? savedSession : s));
            setCurrentSessionId(newSessionId);
          }
        }
      }
      // Extract actual text content from the file
      let extractedText = '';
      try {
        extractedText = await extractTextFromFile(uploadedFile!);

        // Store the extracted content
        setFileContent(extractedText);
        setConfig(prev => ({
          ...prev,
          documentContent: extractedText
        }));

      } catch (extractError: any) {
        console.error('Extraction error:', extractError);
        setErrorMessage(`Failed to extract text from ${uploadedFile!.name}: ${extractError.message}`);
        setIsProcessing(false);
        return;
      }
      // Update progress after extraction
      if (isMountedRef.current) {
        setProcessingProgress(60);
      }
      // Update user credits after successful extraction
      setUserCredits(prev => prev - creditCost);
      const maxLength = 28000; // Increased for better context
      let truncatedContent = extractedText.trim();
      if (truncatedContent.length > maxLength) {
        truncatedContent = truncatedContent.substring(0, maxLength) + '\n\n[Document truncated. The rest of the content could not be included due to length limits.]';
      }
      const fileNameWithoutExt = uploadedFile!.name.replace(/\.[^/.]+$/, '');
      const subjectFromFilename = fileNameWithoutExt.replace(/[_-]/g, ' ');
      // Strengthened system message
      const systemMessage = `You are an extremely accurate exam question generator.
You MUST ONLY create questions and answers that are directly supported by the provided document text.
You are NOT allowed to use outside knowledge or invent facts.

Rules you MUST follow:
1. Every question must be answerable using ONLY the text in <document>...</document>
2. If a concept is not clearly explained in the document → do NOT create a question about it
3. Preserve technical terms, definitions, formulas, names exactly as written
4. For MCQs: distractors must be plausible but clearly wrong according to the document
5. Always prefer specific questions over vague/generic ones
6. Number of questions = exactly the number requested (do not add extra)
7. Questions should be phrased naturally, as in a standard exam, without references to 'the document', 'as stated in the text', 'according to the document', or similar phrases.
8. Generate intelligent questions that test comprehension, analysis, and application.

Output format: valid JSON only, no extra text before or after.

{
  "summary": "short 2–4 sentence summary of main content",
  "keyTopics": ["Topic 1", "Topic 2", "Topic 3"],
  "difficulty_assessment": "easy/medium/hard",
  "estimated_study_time_minutes": 15,
  "questions": [
    {
      "id": "q1",
      "type": "mcq",
      "question": "Question text?",
      "options": ["A", "B", "C", "D"],
      "correctAnswer": 0,
      "explanation": "Explanation",
      "difficulty": "medium",
      "topic": "Topic",
      "concept_tested": "Specific concept"
    }
  ]
}`;
      const prompt = `Document: "${uploadedFile!.name}"
Subject appears to be: ${subjectFromFilename}
<document>
${truncatedContent}
</document>

Based on this document content, generate exactly ${config.questionCount} intelligent questions.
Question Types: ${Object.entries(config.questionTypes).filter(([_, enabled]) => enabled).map(([type]) => type).join(', ')}
Difficulty: ${config.difficultyLevel}
Format: ${config.questionFormat}
${config.focusTopics.length > 0 ? `Focus on these topics: ${config.focusTopics.join(', ')}` : ''}
${config.excludeTopics.length > 0 ? `Exclude these topics: ${config.excludeTopics.join(', ')}` : ''}
Create questions that test true understanding of the material from the document.
Return only valid JSON. No explanations outside the JSON.`;

      const response = await callOpenRouterApi(prompt, userProfile, systemMessage);

      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
      }

      setProcessingProgress(100);

      // Parse JSON
      let cleanResponse = response.trim();
      cleanResponse = cleanResponse.replace(/```json\n?/g, '').replace(/```\n?/g, '');

      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        cleanResponse = jsonMatch[0];
      }

      let extractedData;
      try {
        extractedData = JSON.parse(cleanResponse);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);

        setErrorMessage('Failed to parse generated questions. Please try again.');
        setIsProcessing(false);
        return; // Avoid fallback to generic questions
      }
      // Validate and enhance questions
      if (!extractedData.questions || extractedData.questions.length === 0) {
        throw new Error('No questions generated');
      }
      extractedData.questions = extractedData.questions.map((q: any, idx: number) => {
        if (q.type === 'mcq') {
          if (!q.options || q.options.length < 4) {
            q.options = ['Option A', 'Option B', 'Option C', 'Option D'];
          }
          if (q.correctAnswer === undefined) {
            q.correctAnswer = 0;
          }
        }
        if (!q.id) q.id = `q${idx+1}`;
        if (!q.explanation && config.includeExplanations) {
          q.explanation = 'This concept is fundamental to understanding the material.';
        }
        return q;
      });
      // Create exam state with timer
      const newQuestionState: DocumentQuestionState = {
        questions: extractedData.questions,
        currentQuestion: 0,
        answers: Array(extractedData.questions.length).fill(null),
        isComplete: false,
        startTime: new Date(),
        endTime: null,
        showResults: false,
        selectedQuestions: new Set(extractedData.questions.map((q: any) => q.id)),
        documentInfo: {
          filename: uploadedFile!.name,
          fileSize: uploadedFile!.size,
          fileType: uploadedFile!.type,
          pageCount: undefined,
          processedAt: new Date()
        },
        timeLimit: config.timeLimit,
        timeLeft: config.timeLimit * 60,
        showExplanationForCurrent: false
      };
      if (isMountedRef.current) {
        setQuestionState(newQuestionState);
        setExtractionStarted(true);
      }
      // Update session
      await updateSessionResults(newSessionId, {
        processing_completed: true,
        extracted_questions: extractedData.questions,
        document_summary: extractedData.summary,
        key_topics: extractedData.keyTopics,
        difficulty_assessment: extractedData.difficulty_assessment,
        estimated_study_time: extractedData.estimated_study_time_minutes
      });
      // Add summary message
      const summaryMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `## 📚 Document Analysis Complete! ✅
**Document:** ${uploadedFile!.name}
**File Size:** ${formatFileSize(uploadedFile!.size)}
**Difficulty Assessment:** ${extractedData.difficulty_assessment || config.difficultyLevel}
**Time Limit:** ${config.timeLimit} minutes
### 📊 Document Summary
${extractedData.summary || `This document covers ${subjectFromFilename}.`}
### 🔑 Key Topics
${extractedData.keyTopics?.map((topic: string) => `- **${topic}**`).join('\n') || `- **${subjectFromFilename}**`}
### 📝 Generated Questions (${extractedData.questions.length})
${extractedData.questions.slice(0, 3).map((q: any, idx: number) => {
  return `- **Q${idx+1}** (${q.type}): ${q.question.substring(0, 60)}...`;
}).join('\n')}
${extractedData.questions.length > 3 ? `- ... and ${extractedData.questions.length - 3} more questions` : ''}
### 📋 Exam Instructions:
1. **Timer:** You have ${config.timeLimit} minutes to complete all questions
2. **Navigation:** Use Previous/Next buttons or question grid
3. **Answering:** Select options for MCQ, type for theory, click for True/False
4. **Progress:** Track your progress with the timer and question grid
5. **Submit:** Click Submit when done or timer will auto-submit
6. **Results:** After submission, view your score and detailed explanations for all questions
**Ready to test your knowledge? Start the exam below!** 🎯`,
        timestamp: new Date(),
        chat_type: 'document-questions',
      };
      const updatedMessages = [...newMessages, summaryMessage];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(newSessionId, 'assistant', summaryMessage.content);
    } catch (error: any) {
      console.error('Error:', error);

      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
      }
      setProcessingProgress(0);

      setErrorMessage('Failed to extract questions. Please try again.');
      setErrorDetails(error.message);

      const errorMsg: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date(),
        chat_type: 'document-questions',
      };

      const updatedMessages = [...messages, errorMsg];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(newSessionId, 'assistant', errorMsg.content);
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };
  
  // ===== EXAM FUNCTIONS =====
  const handleQuestionAnswer = (answer: any) => {
    if (!questionState || questionState.isComplete) return;
    
    const newAnswers = [...questionState.answers];
    newAnswers[questionState.currentQuestion] = answer;
    
    if (isMountedRef.current) {
      setQuestionState(prev => prev ? {
        ...prev,
        answers: newAnswers,
        // Don't show explanation immediately - only after submit
        showExplanationForCurrent: false
      } : null);
    }
  };
  
  const navigateToQuestion = (index: number) => {
    if (!questionState || questionState.isComplete) return;

    if (isMountedRef.current) {
      setQuestionState(prev => prev ? {
        ...prev,
        currentQuestion: index,
        // Reset explanation display when navigating
        showExplanationForCurrent: false
      } : null);
    }
  };
  
  const goToPrevious = () => {
    if (!questionState || questionState.currentQuestion === 0) return;

    if (isMountedRef.current) {
      setQuestionState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion - 1,
        // Reset explanation display when navigating
        showExplanationForCurrent: false
      } : null);
    }
  };
  
  const goToNext = () => {
    if (!questionState || questionState.currentQuestion === questionState.questions.length - 1) return;

    if (isMountedRef.current) {
      setQuestionState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        // Reset explanation display when navigating
        showExplanationForCurrent: false
      } : null);
    }
  };
  
  const handleSubmitExam = () => {
    if (!questionState || isLoading) return;

    if (window.confirm(`Submit exam? ${questionState.answers.filter(a => a !== null).length}/${questionState.questions.length} answered.`)) {
      if (isMountedRef.current) {
        setIsLoading(true);

        // Calculate score
        let correct = 0;
        questionState.questions.forEach((q, idx) => {
          const answer = questionState.answers[idx];
          if (q.type === 'mcq' && answer === q.correctAnswer) correct++;
          else if (q.type === 'true-false' && answer === q.correctAnswer) correct++;
          else if (q.type === 'fill-blank' && answer?.toLowerCase() === (q.correctAnswer as string)?.toLowerCase()) correct++;
          // For theory questions, we don't automatically grade them
        });

        const score = Math.round((correct / questionState.questions.length) * 100);

        setQuestionState(prev => prev ? {
          ...prev,
          isComplete: true,
          endTime: new Date(),
          score: score,
          // Show explanations after submission
          showExplanationForCurrent: true
        } : null);

        // Add results message with detailed feedback
        const resultsMessage: ChatMessageType = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `## 📊 Exam Results\n\n**Score:** ${correct}/${questionState.questions.length} (${score}%)\n**Time Taken:** ${Math.round((config.timeLimit * 60 - questionState.timeLeft) / 60)} minutes\n\n### 📝 Detailed Feedback\n\n${questionState.questions.map((q, idx) => {
            const userAnswer = questionState.answers[idx];
            const isCorrect = q.type === 'mcq' ? userAnswer === q.correctAnswer :
                             q.type === 'true-false' ? userAnswer === q.correctAnswer :
                             q.type === 'fill-blank' ? userAnswer?.toLowerCase() === (q.correctAnswer as string)?.toLowerCase() :
                             false; // Theory questions not automatically graded
            
            const correctAnswerText = q.type === 'mcq' && q.options ? 
              q.options[q.correctAnswer as number] : 
              q.correctAnswer?.toString() || 'N/A';
            
            const userAnswerText = q.type === 'mcq' && q.options && typeof userAnswer === 'number' ?
              q.options[userAnswer] :
              userAnswer?.toString() || 'Not answered';
            
            return `**Q${idx + 1}:** ${isCorrect ? '✅' : '❌'} ${q.question}\n` +
                   `- Your answer: ${userAnswerText}\n` +
                   `- Correct answer: ${correctAnswerText}\n` +
                   `- ${q.explanation ? `Explanation: ${q.explanation}` : ''}\n`;
          }).join('\n')}`,
          timestamp: new Date(),
          chat_type: 'document-questions',
        };

        setMessages(prev => [...prev, resultsMessage]);
        saveChatMessage(currentSessionId, 'assistant', resultsMessage.content);

        setIsLoading(false);
      }
    }
  };
  
  const toggleQuestionSelection = (questionId: string) => {
    if (!questionState) return;

    const newSelected = new Set(questionState.selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }

    if (isMountedRef.current) {
      setQuestionState(prev => prev ? {
        ...prev,
        selectedQuestions: newSelected
      } : null);
    }
  };
  
  const exportQuestions = () => {
    if (!questionState) return;

    const selectedQuestions = questionState.questions.filter(q =>
      questionState.selectedQuestions.has(q.id)
    );

    const exportData = {
      documentName: uploadedFile?.name,
      exportDate: new Date().toISOString(),
      questionCount: selectedQuestions.length,
      questions: selectedQuestions
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `questions-from-${uploadedFile?.name.replace(/\.[^/.]+$/, '')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // ===== CHAT FUNCTIONS =====
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isLoading) return;

    if (isMountedRef.current) {
      setIsLoading(true);
    }
    setErrorMessage('');

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: chatInput,
      timestamp: new Date(),
      chat_type: 'document-questions',
    };

    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(currentSessionId, 'user', chatInput);
    setChatInput('');

    try {
      const systemMessage = "You are a helpful assistant for Document Question Extractor. Answer questions about the extracted questions, provide explanations, and help students understand the material.";

      const response = await callOpenRouterApi(chatInput, userProfile, systemMessage);

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        chat_type: 'document-questions',
      };

      if (isMountedRef.current) {
        setMessages(prev => [...prev, assistantMessage]);
      }
      await saveChatMessage(currentSessionId, 'assistant', response);

    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Failed to get response. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };
  
  // ===== SESSION MANAGEMENT =====
  const switchToSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      if (isMountedRef.current) {
        setCurrentSessionId(sessionId);
        setMessages(session.messages);
        setExtractionStarted(false);
        setQuestionState(null);
        setUploadedFile(null);
        setFileContent('');

        if (session.processing_completed && session.extracted_questions) {
          setExtractionStarted(true);
          setQuestionState({
            questions: session.extracted_questions,
            currentQuestion: 0,
            answers: [],
            isComplete: false,
            startTime: new Date(),
            endTime: null,
            showResults: false,
            selectedQuestions: new Set(session.extracted_questions.map(q => q.id)),
            documentInfo: null,
            timeLimit: config.timeLimit,
            timeLeft: config.timeLimit * 60,
            showExplanationForCurrent: false
          });
        }
      }
    }
  };
  
  const deleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.isTemporary) {
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    } else {
      await supabase.from('document_question_sessions').delete().eq('id', sessionId);
      await supabase.from('document_question_messages').delete().eq('session_id', sessionId);
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    }
    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        switchToSession(remainingSessions[0].id);
      } else {
        createNewSession();
      }
    }
  };
  
  // ===== RENDER FUNCTIONS =====
  const renderCreditDisplay = () => (
    <div className="credit-display">
      <div className="credit-info">
        <div className="credit-icon">💰</div>
        <div className="credit-details">
          <div className="credit-count">{userCredits} credits</div>
          <div className="subscription-tier">{subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan</div>
        </div>
        {userCredits < 5 && (
          <button
            className="get-more-credits-btn"
            onClick={() => setShowSubscriptionModal(true)}
          >
            Get More
          </button>
        )}
      </div>
      {userCredits < 3 && (
        <div className="credit-warning">
          ⚠️ Low credits! Processing costs {calculateCreditCost()} credit(s).
        </div>
      )}
    </div>
  );
  
  const renderFileUpload = () => (
    <div className="file-upload-section">
      <div className="upload-header">
        <h3>📄 Upload Document</h3>
        <p className="upload-description">Upload a document to extract questions automatically</p>
      </div>

      {!uploadedFile ? (
        <div
          className={`upload-area ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept=".txt,.pdf,.doc,.docx,.md,.csv"
            style={{ display: 'none' }}
          />
          <div className="upload-icon">📤</div>
          <div className="upload-text">
            <strong>Click to upload</strong> or drag and drop
          </div>
          <div className="upload-hint">
            Supported: TXT, PDF, DOC, DOCX, MD, CSV (Max 10MB)
          </div>
        </div>
      ) : (
        <div className="file-info">
          <div className="file-details">
            <div className="file-icon">📄</div>
            <div className="file-meta">
              <div className="file-name">{uploadedFile.name}</div>
              <div className="file-size">{formatFileSize(uploadedFile.size)}</div>
            </div>
            <button className="remove-file-btn" onClick={removeFile}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
  
  const renderQuestionTypes = () => (
    <div className="question-types-section">
      <h4>Question Types</h4>
      <div className="type-grid">
        {[
          { key: 'mcq', label: 'Multiple Choice', desc: '4 options' },
          { key: 'theory', label: 'Theory', desc: 'Open-ended' },
          { key: 'trueFalse', label: 'True/False', desc: 'Boolean' },
          { key: 'fillBlank', label: 'Fill Blank', desc: 'Complete' },
          { key: 'matching', label: 'Matching', desc: 'Match items' }
        ].map(item => (
          <label key={item.key} className="type-checkbox">
            <input
              type="checkbox"
              checked={config.questionTypes[item.key as keyof typeof config.questionTypes]}
              onChange={() => handleQuestionTypeChange(item.key as keyof typeof config.questionTypes)}
            />
            <span className="checkmark"></span>
            <span className="type-label">{item.label}</span>
            <span className="type-desc">{item.desc}</span>
          </label>
        ))}
      </div>
    </div>
  );
  
  const renderConfigForm = () => (
    <div className="config-form-section">
      <h4>Configuration</h4>

      <div className="form-row">
        <div className="form-group">
          <label>Questions (1-50)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={tempQuestionCount}
            onChange={handleQuestionCountChange}
            onBlur={handleQuestionCountBlur}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Time Limit (minutes)</label>
          <input
            type="number"
            min="5"
            max="180"
            value={tempTimeLimit}
            onChange={handleTimeLimitChange}
            onBlur={handleTimeLimitBlur}
            className="form-input"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Difficulty</label>
          <select
            value={config.difficultyLevel}
            onChange={(e) => handleDifficultyChange(e.target.value as any)}
            className="form-input"
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>

        <div className="form-group">
          <label>Format</label>
          <select
            value={config.questionFormat}
            onChange={(e) => handleFormatChange(e.target.value as any)}
            className="form-input"
          >
            <option value="exam">Exam</option>
            <option value="practice">Practice</option>
            <option value="quiz">Quiz</option>
            <option value="homework">Homework</option>
          </select>
        </div>
      </div>

      <div className="form-group full-width">
        <label>
          <input
            type="checkbox"
            checked={config.includeExplanations}
            onChange={(e) => setConfig(prev => ({...prev, includeExplanations: e.target.checked}))}
          />
          <span> Include explanations</span>
        </label>
      </div>

      <div className="form-group full-width">
        <label>Focus Topics (comma-separated)</label>
        <input
          type="text"
          value={tempFocusTopics}
          onChange={(e) => setTempFocusTopics(e.target.value)}
          onBlur={handleFocusTopicsBlur}
          placeholder="e.g., Algebra, Cell Biology"
          className="form-input"
        />
      </div>

      <div className="form-group full-width">
        <label>Exclude Topics (comma-separated)</label>
        <input
          type="text"
          value={tempExcludeTopics}
          onChange={(e) => setTempExcludeTopics(e.target.value)}
          onBlur={handleExcludeTopicsBlur}
          placeholder="e.g., Advanced topics"
          className="form-input"
        />
      </div>
    </div>
  );
  
  const renderProcessingProgress = () => (
    <div className="processing-progress">
      <div className="progress-header">
        <div className="progress-spinner"></div>
        <h4>Processing Document...</h4>
      </div>
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{width: `${processingProgress}%`}}
        ></div>
      </div>
      <div className="progress-status">
        {processingProgress < 30 && "📖 Reading document..."}
        {processingProgress >= 30 && processingProgress < 60 && "🔍 Extracting text..."}
        {processingProgress >= 60 && processingProgress < 90 && "✍️ Generating questions..."}
        {processingProgress >= 90 && "✅ Finalizing..."}
      </div>
    </div>
  );
  
  const renderExamInterface = () => {
    if (!questionState) return null;

    const currentQ = questionState.questions[questionState.currentQuestion];
    const answeredCount = questionState.answers.filter(a => a !== null).length;

    return (
      <div className="exam-interface">
        <div className="exam-header">
          <div className="exam-timer">
            <div className="timer-circle">
              <svg width="60" height="60" viewBox="0 0 60 60">
                <circle cx="30" cy="30" r="26" fill="none" stroke="#e0e0e0" strokeWidth="4" />
                <circle
                  cx="30" cy="30" r="26"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="4"
                  strokeDasharray={`${2 * Math.PI * 26}`}
                  strokeDashoffset={`${2 * Math.PI * 26 * (1 - answeredCount / questionState.questions.length)}`}
                  transform="rotate(-90 30 30)"
                />
              </svg>
              <span className="timer-text">{answeredCount}/{questionState.questions.length}</span>
            </div>
            <div className="timer-info">
              <div className="timer-digits">{formatTime(questionState.timeLeft)}</div>
              <div className="timer-label">Time Left</div>
            </div>
          </div>

          <div className="exam-stats">
            <div className="stat-badge">
              <span className="stat-icon">📊</span>
              <span className="stat-value">{questionState.questions.length} Questions</span>
            </div>
            <div className="stat-badge">
              <span className="stat-icon">⏱️</span>
              <span className="stat-value">{config.difficultyLevel}</span>
            </div>
          </div>
        </div>
        <div className="exam-question-area">
          <div className="question-header">
            <span className="question-number">Q{questionState.currentQuestion + 1}/{questionState.questions.length}</span>
            <span className={`question-type-badge ${currentQ.type}`}>{currentQ.type.replace('-', ' ')}</span>
            {currentQ.difficulty && (
              <span className={`difficulty-badge ${currentQ.difficulty}`}>{currentQ.difficulty}</span>
            )}
          </div>
          <div className="question-text">
            <MarkdownRenderer content={currentQ.question} theme={theme} />
          </div>
          {currentQ.type === 'mcq' && currentQ.options && (
            <div className="mcq-options">
              {currentQ.options.map((option, idx) => (
                <div
                  key={idx}
                  className={`mcq-option ${questionState.answers[questionState.currentQuestion] === idx ? 'selected' : ''} ${questionState.isComplete ? 'disabled' : ''}`}
                  onClick={() => !questionState.isComplete && handleQuestionAnswer(idx)}
                >
                  <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                  <span className="option-text">{option}</span>
                </div>
              ))}
            </div>
          )}
          {currentQ.type === 'theory' && (
            <div className="theory-answer">
              <textarea
                className="theory-input"
                placeholder="Type your answer here..."
                value={questionState.answers[questionState.currentQuestion] || ''}
                onChange={(e) => !questionState.isComplete && handleQuestionAnswer(e.target.value)}
                disabled={questionState.isComplete}
                rows={6}
              />
            </div>
          )}
          {currentQ.type === 'true-false' && (
            <div className="true-false-options">
              <div
                className={`tf-option ${questionState.answers[questionState.currentQuestion] === true ? 'selected' : ''}`}
                onClick={() => !questionState.isComplete && handleQuestionAnswer(true)}
              >
                True
              </div>
              <div
                className={`tf-option ${questionState.answers[questionState.currentQuestion] === false ? 'selected' : ''}`}
                onClick={() => !questionState.isComplete && handleQuestionAnswer(false)}
              >
                False
              </div>
            </div>
          )}
          {currentQ.type === 'fill-blank' && (
            <div className="fill-blank-answer">
              <input
                type="text"
                className="fill-blank-input"
                placeholder="Enter your answer..."
                value={questionState.answers[questionState.currentQuestion] || ''}
                onChange={(e) => !questionState.isComplete && handleQuestionAnswer(e.target.value)}
                disabled={questionState.isComplete}
              />
            </div>
          )}
          {currentQ.type === 'matching' && currentQ.options && (
            <div className="matching-answer">
              {/* Basic implementation for matching - assume options are pairs */}
              <p>Match the following:</p>
              {/* You may need to adjust based on how matching questions are structured in JSON */}
              <select
                className="matching-select"
                onChange={(e) => !questionState.isComplete && handleQuestionAnswer(e.target.value)}
                disabled={questionState.isComplete}
              >
                <option value="">Select match</option>
                {currentQ.options.map((option, idx) => (
                  <option key={idx} value={idx}>{option}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Only show explanation after exam is complete */}
          {questionState.isComplete && questionState.showExplanationForCurrent && currentQ.explanation && (
            <div className="question-explanation">
              <div className="explanation-header">📖 Explanation & Feedback</div>
              <div className="explanation-content">
                <MarkdownRenderer content={currentQ.explanation} theme={theme} />
                {questionState.answers[questionState.currentQuestion] !== null && (
                  <div className="answer-feedback">
                    <strong>Your answer:</strong> {
                      currentQ.type === 'mcq' && currentQ.options && typeof questionState.answers[questionState.currentQuestion] === 'number' ?
                        currentQ.options[questionState.answers[questionState.currentQuestion]] :
                        questionState.answers[questionState.currentQuestion]?.toString() || 'Not answered'
                    }
                    <br />
                    <strong>Correct answer:</strong> {
                      currentQ.type === 'mcq' && currentQ.options ?
                        currentQ.options[currentQ.correctAnswer as number] :
                        currentQ.correctAnswer?.toString() || 'N/A'
                    }
                    <br />
                    <strong>Status:</strong> {
                      (currentQ.type === 'mcq' && questionState.answers[questionState.currentQuestion] === currentQ.correctAnswer) ||
                      (currentQ.type === 'true-false' && questionState.answers[questionState.currentQuestion] === currentQ.correctAnswer) ||
                      (currentQ.type === 'fill-blank' && questionState.answers[questionState.currentQuestion]?.toLowerCase() === (currentQ.correctAnswer as string)?.toLowerCase())
                      ? '✅ Correct' : '❌ Incorrect'
                    }
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="exam-navigation">
            <button
              className="nav-btn prev"
              onClick={goToPrevious}
              disabled={questionState.currentQuestion === 0 || questionState.isComplete}
            >
              ← Previous
            </button>

            <div className="nav-center">
              {!questionState.isComplete ? (
                <button
                  className="nav-btn submit"
                  onClick={handleSubmitExam}
                  disabled={isLoading}
                >
                  📝 Submit Exam
                </button>
              ) : (
                <div className="score-badge">
                  Score: {questionState.score}%
                </div>
              )}
            </div>

            <button
              className="nav-btn next"
              onClick={goToNext}
              disabled={questionState.currentQuestion === questionState.questions.length - 1 || questionState.isComplete}
            >
              Next →
            </button>
          </div>
        </div>
        <div className="question-navigator">
          <h4>Question Navigator</h4>
          <div className="question-grid">
            {questionState.questions.map((_, idx) => (
              <button
                key={idx}
                className={`question-grid-item
                  ${questionState.currentQuestion === idx ? 'current' : ''}
                  ${questionState.answers[idx] !== null ? 'answered' : 'unanswered'}
                  ${questionState.isComplete ? 'complete' : ''}
                `}
                onClick={() => navigateToQuestion(idx)}
                disabled={questionState.isComplete}
              >
                {idx + 1}
              </button>
            ))}
          </div>
          <div className="legend">
            <span><span className="legend-dot current"></span> Current</span>
            <span><span className="legend-dot answered"></span> Answered</span>
            <span><span className="legend-dot unanswered"></span> Unanswered</span>
          </div>

          {questionState.selectedQuestions.size > 0 && (
            <button className="export-btn" onClick={exportQuestions}>
              📥 Export Selected ({questionState.selectedQuestions.size})
            </button>
          )}
        </div>
        {questionState.isComplete && questionState.score !== undefined && (
          <div className="exam-results">
            <h3>📊 Your Results</h3>
            <div className="score-circle">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e0e0e0" strokeWidth="8" />
                <circle
                  cx="60" cy="60" r="54"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="8"
                  strokeDasharray={`${2 * Math.PI * 54}`}
                  strokeDashoffset={`${2 * Math.PI * 54 * (1 - questionState.score / 100)}`}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="75" textAnchor="middle" fill="currentColor" fontSize="28" fontWeight="bold">
                  {questionState.score}%
                </text>
              </svg>
            </div>
            <div className="score-details">
              <p>Correct: <strong>{questionState.answers.filter((a, i) => {
                const q = questionState.questions[i];
                if (q.type === 'mcq') return a === q.correctAnswer;
                if (q.type === 'true-false') return a === q.correctAnswer;
                if (q.type === 'fill-blank') return a?.toLowerCase() === (q.correctAnswer as string)?.toLowerCase();
                return false;
              }).length}/{questionState.questions.length}</strong></p>
              <p>Time Taken: <strong>{Math.round((config.timeLimit * 60 - questionState.timeLeft) / 60)} minutes</strong></p>
            </div>
            <p className="results-note">Click on any question to see detailed feedback and explanations.</p>
          </div>
        )}
      </div>
    );
  };
  
  const renderSidebar = () => (
    <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="logo">📋</div>
        {sidebarOpen && <span className="app-name">Doc Questions</span>}
      </div>

      {sidebarOpen && (
        <>
          <div className="theme-toggle-sidebar">
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>

          {renderCreditDisplay()}

          <button className="new-doc-btn" onClick={createNewSession}>
            ➕ New Document
          </button>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="session-history">
            <h4>Document History</h4>
            {sessions
              .filter(session =>
                session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                session.documentName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((session) => (
                <div
                  key={session.id}
                  className={`session-item ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => switchToSession(session.id)}
                >
                  <div className="session-icon">📄</div>
                  <div className="session-info">
                    <div className="session-title">
                      {session.title}
                      {session.isTemporary && ' (New)'}
                    </div>
                    <div className="session-meta">
                      <span className="session-date">
                        {new Date(session.timestamp).toLocaleDateString()}
                      </span>
                      <span className="question-count">
                        {session.questionCount} Q
                      </span>
                    </div>
                  </div>
                  <button
                    className="delete-session-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
  
  // ===== MAIN RENDER =====
  return (
    <div className={`document-question-extractor ${theme}`}>
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentCredits={userCredits}
      />
      <div className="extractor-layout">
        {renderSidebar()}
        <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <div className="content-header">
            <h1 className="main-title">📋 Document Question Extractor</h1>
            <p className="subtitle">Upload any document and automatically generate intelligent exam questions</p>

            <div className="mobile-credit-display">
              {renderCreditDisplay()}
            </div>
          </div>
          {errorMessage && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <span className="error-text">{errorMessage}</span>
                {errorDetails && (
                  <span className="error-details">({errorDetails})</span>
                )}
              </div>
              {errorMessage.includes('credits') && (
                <button
                  className="error-upgrade-btn"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  Get More Credits
                </button>
              )}
            </div>
          )}
          <div className="content-area">
            {isCheckingCredits ? (
              <div className="loading-credits">
                <div className="loading-spinner-large"></div>
                <p>Loading your credits...</p>
              </div>
            ) : !extractionStarted ? (
              <div className="upload-config-container">
                {renderFileUpload()}

                {uploadedFile && (
                  <>
                    {renderQuestionTypes()}
                    {renderConfigForm()}

                    <div className="action-buttons">
                      <button
                        className="extract-btn"
                        onClick={handleExtractQuestions}
                        disabled={isProcessing || !uploadedFile}
                      >
                        {isProcessing ? (
                          <>
                            <span className="loading-spinner"></span>
                            Processing...
                          </>
                        ) : (
                          `🚀 Generate Exam (${calculateCreditCost()} credit${calculateCreditCost() > 1 ? 's' : ''})`
                        )}
                      </button>

                      <div className="cost-breakdown">
                        <div className="cost-item">Base cost: 1 credit</div>
                        {uploadedFile && uploadedFile.size > 1024 * 1024 && (
                          <div className="cost-item">+{Math.floor(uploadedFile.size / (1024 * 1024))} (file size)</div>
                        )}
                        {config.questionCount > 20 && (
                          <div className="cost-item">+{Math.floor((config.questionCount - 20) / 10)} (extra questions)</div>
                        )}
                        <div className="cost-total">Total: {calculateCreditCost()} credits</div>
                      </div>
                    </div>

                    {isProcessing && renderProcessingProgress()}
                  </>
                )}
              </div>
            ) : (
              renderExamInterface()
            )}
            {(extractionStarted || messages.length > 0) && (
              <div className="chat-section">
                <h3>💬 Discussion & Help</h3>
                <div className="chat-messages-container" ref={chatContainerRef}>
                  {messages.map((message) => (
                    <div key={message.id} className="message-wrapper">
                      <ChatMessage
                        message={message}
                        onSpeak={(text) => speak(text, message.id)}
                        isSpeaking={isMessageSpeaking(message.id)}
                        onShare={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/document/message/${message.id}`);
                        }}
                      />
                    </div>
                  ))}
                  {isLoading && (
                    <div className="typing-indicator">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="typing-text">Assistant is thinking...</span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="chat-input-container">
                  <InputField
                    value={chatInput}
                    onChange={setChatInput}
                    onSubmit={handleChatSubmit}
                    loading={isLoading}
                    placeholder="Ask about the questions or document content..."
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentQuestionExtractor;